import { AudioLines, ScanFace, Sparkles, Users } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { PageShell } from '@/components/layout/PageShell'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

export type IdentityTab = 'identities' | 'unassigned' | 'unassigned-voices' | 'matches'

/**
 * What each tab shows. The face tabs are Advanced Face Detection's (ADR-0051)
 * and the voices tab is HUMINT's (ADR-0077): a tab whose slot is absent is not
 * offered, and the directory alone shows with no tab bar.
 */
export interface IdentityWorkspaceContent {
  identities: ReactNode
  unassigned?: ReactNode
  'unassigned-voices'?: ReactNode
  matches?: ReactNode
}

interface IdentityWorkspaceProps {
  activeTab: IdentityTab
  onTabChange: (tab: IdentityTab) => void
  content: IdentityWorkspaceContent
}

/** The tab an absent slot leaves the workspace on: the directory. */
function presentTab(tab: IdentityTab, content: IdentityWorkspaceContent): IdentityTab {
  return content[tab] === undefined ? 'identities' : tab
}

/** One navigation and one page heading for the identity, face and matching workflows. */
export function IdentityWorkspace(props: IdentityWorkspaceProps) {
  const { t } = useTranslation()
  const { content } = props
  const hasTabs =
    content.unassigned !== undefined ||
    content['unassigned-voices'] !== undefined ||
    content.matches !== undefined
  return (
    <PageShell
      icon={Users}
      title={t('cases.investigate.identity.title')}
      summary={t('cases.investigate.identity.summary')}
    >
      {hasTabs ? <WorkspaceTabs {...props} /> : content.identities}
    </PageShell>
  )
}

function WorkspaceTabs({ activeTab, onTabChange, content }: IdentityWorkspaceProps) {
  const { t } = useTranslation()
  return (
    <Tabs
      value={presentTab(activeTab, content)}
      onValueChange={value => onTabChange(value as IdentityTab)}
      className="gap-6"
    >
      <div className="overflow-x-auto border-b border-border">
        <TabsList variant="line" aria-label={t('identity.workspaceLabel')}>
          <TabsTrigger value="identities" className="gap-2 px-2 py-2.5 sm:px-3">
            <Users className="hidden size-4 sm:block" />
            {t('identity.tabs.identities')}
          </TabsTrigger>
          {content.unassigned !== undefined && (
            <TabsTrigger value="unassigned" className="gap-2 px-2 py-2.5 sm:px-3">
              <ScanFace className="hidden size-4 sm:block" />
              {t('identity.tabs.unassignedFaces')}
            </TabsTrigger>
          )}
          {content['unassigned-voices'] !== undefined && (
            <TabsTrigger value="unassigned-voices" className="gap-2 px-2 py-2.5 sm:px-3">
              <AudioLines className="hidden size-4 sm:block" />
              {t('identity.tabs.unassignedVoices')}
            </TabsTrigger>
          )}
          {content.matches !== undefined && (
            <TabsTrigger value="matches" className="gap-2 px-2 py-2.5 sm:px-3">
              <Sparkles className="hidden size-4 sm:block" />
              {t('identity.tabs.faceMatches')}
            </TabsTrigger>
          )}
        </TabsList>
      </div>
      <TabsContent value="identities">{content.identities}</TabsContent>
      {content.unassigned !== undefined && (
        <TabsContent value="unassigned">{content.unassigned}</TabsContent>
      )}
      {content['unassigned-voices'] !== undefined && (
        <TabsContent value="unassigned-voices">{content['unassigned-voices']}</TabsContent>
      )}
      {content.matches !== undefined && (
        <TabsContent value="matches">
          <div className="space-y-1 pb-4">
            <h2 className="text-lg font-semibold">{t('identity.tabs.faceMatches')}</h2>
            <p className="text-sm text-muted-foreground">{t('identity.faceMatchesHint')}</p>
          </div>
          {content.matches}
        </TabsContent>
      )}
    </Tabs>
  )
}
